import { FC } from 'react';
import { Award, ClipboardList, Clock, Leaf, Sparkles, ShieldCheck } from 'lucide-react';
import { Container } from '../../layout/Container';
import { BenefitItem, BenefitItemProps } from './BenefitItem';
import { cn } from '../../../utils/cn';

/**
 * Benefits data for the massage therapy services
 */
const benefits: Omit<BenefitItemProps, 'className'>[] = [
  {
    icon: Award,
    title: 'Licensed & Certified',
    description:
      'All our therapists are fully licensed professionals with extensive training and certifications in various massage techniques.',
  },
  {
    icon: ClipboardList,
    title: 'Personalized Plans',
    description:
      'We create customized treatment plans tailored to your specific needs, whether for relaxation, pain relief, or rehabilitation.',
  },
  {
    icon: Clock,
    title: 'Flexible Scheduling',
    description:
      'Book appointments at times that work for you with our easy online scheduling system, including evening and weekend availability.',
  },
  {
    icon: Leaf,
    title: 'Relaxing Environment',
    description:
      'Experience tranquility in our thoughtfully designed space with soothing ambiance, aromatherapy, and calming music.',
  },
  {
    icon: Sparkles,
    title: 'Premium Products',
    description:
      'We use only high-quality, organic oils and lotions that are gentle on your skin and enhance your massage experience.',
  },
  {
    icon: ShieldCheck,
    title: 'Satisfaction Guaranteed',
    description:
      'Your well-being is our priority. We stand behind our services with a commitment to your complete satisfaction.',
  },
];

export interface BenefitsSectionProps {
  /**
   * Optional section title
   * @default "Why Choose Us"
   */
  title?: string;
  /**
   * Optional section subtitle
   */
  subtitle?: string;
  /**
   * Optional additional className for the section
   */
  className?: string;
}

/**
 * BenefitsSection component showcasing the benefits of massage therapy services.
 * Displays a responsive grid of benefit items with icons, titles, and descriptions.
 *
 * Layout:
 * - Desktop (lg+): 3 columns (2x3 grid)
 * - Tablet (md): 2 columns (2x3 grid)
 * - Mobile: 1 column
 *
 * @example
 * <BenefitsSection />
 *
 * @example
 * <BenefitsSection
 *   title="The Benefits of Massage Therapy"
 *   subtitle="Discover why thousands trust us"
 * />
 */
export const BenefitsSection: FC<BenefitsSectionProps> = ({
  title = 'Why Choose Us',
  subtitle = 'Experience the difference with our professional massage therapy services',
  className,
}) => {
  return (
    <section
      className={cn('bg-gray-50 dark:bg-slate-900 py-16 md:py-20 lg:py-24', className)}
      aria-labelledby="benefits-section-title"
    >
      <Container size="xl">
        {/* Section header */}
        <div className="mx-auto mb-12 max-w-2xl text-center md:mb-16">
          <h2
            id="benefits-section-title"
            className="mb-4 font-serif text-3xl font-bold tracking-tight text-gray-900 dark:text-slate-100 md:text-4xl"
          >
            {title}
          </h2>
          {subtitle && <p className="text-lg text-gray-600 dark:text-slate-400">{subtitle}</p>}
        </div>

        {/* Benefits grid */}
        <div
          className={cn(
            'grid gap-6',
            'grid-cols-1', // Mobile: 1 column
            'md:grid-cols-2', // Tablet: 2 columns
            'lg:grid-cols-3' // Desktop: 3 columns
          )}
        >
          {benefits.map((benefit, index) => (
            <BenefitItem
              key={benefit.title}
              icon={benefit.icon}
              title={benefit.title}
              description={benefit.description}
              className={cn(
                // Stagger animation delay for visual interest
                'animate-fade-in-up',
                index === 0 && 'animation-delay-0',
                index === 1 && 'animation-delay-100',
                index === 2 && 'animation-delay-200',
                index === 3 && 'animation-delay-300',
                index === 4 && 'animation-delay-400',
                index === 5 && 'animation-delay-500'
              )}
            />
          ))}
        </div>
      </Container>
    </section>
  );
};

BenefitsSection.displayName = 'BenefitsSection';
